/**
 * Rewrites every hast element whose name React's server renderer would reject.
 *
 * React DOM validates element names against `/^[a-zA-Z][a-zA-Z:_.\-\d]*$/` and
 * THROWS `Invalid tag: <name>` on a miss (`startChunkForTag`, react-dom/server).
 * The browser's `document.createElement` accepts far more, so an element named
 * `liměřítko` renders happily on the client and only kills the server render:
 * the response carries HTTP 500 while the page still looks perfect to a human,
 * and crawlers drop it.
 *
 * `rehype-raw` runs author-entered HTML through a real HTML parser, so a
 * description that lost the `>` of an `<li>` — `<liMěřítko 1:12</li>` — yields
 * an element literally named `liměřítko`. Content is not trusted to be
 * well-formed, so the pipeline has to be total: an element React cannot name
 * becomes a neutral `span` (children kept, the parser's junk attributes
 * dropped), turning corrupt markup into a cosmetic glitch instead of a 500.
 */
const REACT_VALID_TAG_NAME = /^[a-zA-Z][a-zA-Z:_.\-\d]*$/;

const REPLACEMENT_TAG_NAME = 'span';

type NamedElement = {
  type: 'element';
  tagName: string;
  properties?: unknown;
};

const isNamedElement = (node: unknown): node is NamedElement =>
  typeof node === 'object' &&
  node !== null &&
  'type' in node &&
  node.type === 'element' &&
  'tagName' in node &&
  typeof node.tagName === 'string';

const childrenOf = (node: unknown): readonly unknown[] =>
  typeof node === 'object' && node !== null && 'children' in node && Array.isArray(node.children)
    ? node.children
    : [];

const sanitize = (node: unknown): void => {
  if (isNamedElement(node) && !REACT_VALID_TAG_NAME.test(node.tagName)) {
    node.tagName = REPLACEMENT_TAG_NAME;
    node.properties = {};
  }

  for (const child of childrenOf(node)) sanitize(child);
};

export const rehypeSafeTagNames = () => (tree: unknown) => {
  sanitize(tree);
};
